import { useState, useEffect, useCallback } from "react";
import { X, Play } from "lucide-react";
import { Button } from "./ui/button";

interface VideoPlayerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
}

export function VideoPlayerPopup({ isOpen, onClose, videoUrl }: VideoPlayerPopupProps) {
  const [showVideo, setShowVideo] = useState(false);

  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(videoUrl);

  // Memoised and declared before the effect below, which closed over the first
  // render's copy and so kept calling a stale onClose.
  const handleClose = useCallback(() => {
    setShowVideo(false);
    onClose();
  }, [onClose]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  const handlePlayVideo = () => {
    setShowVideo(true);
  };

  if (!isOpen || !videoId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative z-10 w-[80%] mx-4">
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -top-12 right-0 text-white hover:text-white hover:bg-white/20 z-20"
          onClick={handleClose}
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Video container */}
        <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
          <div className="aspect-video">
            {!showVideo ? (
              // Thumbnail with play button
              <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                <img
                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Button
                    size="lg"
                    className="bg-white/90 hover:bg-white text-black rounded-full w-20 h-20 p-0"
                    onClick={handlePlayVideo}
                  >
                    <Play className="h-8 w-8 ml-1" fill="currentColor" />
                  </Button>
                </div>
              </div>
            ) : (
              // YouTube embed
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                title="Demo Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </div>

        {/* Video info */}
        <div className="mt-4 text-center">
          <h3 className="text-white text-lg font-semibold mb-2">
            Decisionr Demo
          </h3>
          <p className="text-white/80 text-sm">
            See how Decisionr helps you make better decisions through systematic tracking and reflection.
          </p>
        </div>
      </div>
    </div>
  );
}